<script lang="ts">
  import { STAGE_KIND_META } from '../pipelines';
  import { rosterMissing } from '../community.svelte';
  import RosterAccessBanner from './RosterAccessBanner.svelte';
  import type { PipelineBoardData, PipelineColumn } from '@api';
  import PipelineCardTile from './PipelineCardTile.svelte';

  // The board of a TOTAL STATE MACHINE: one column per state, and every non-staff
  // member of the community sits in exactly one of them. There is no queue column —
  // under this model nobody is outside a pipeline, so a member the old queue would
  // have listed is simply in the catch-all state (`Silent`, `Not paying`).
  //
  // A MANUAL BOARD (`board.pipeline.manual`) is the exception, and the reason this
  // component grew an `onAdd`. It is authored by the owner and not total: the engine
  // skips it, so it starts empty and a card is there only because someone put it there.
  // Each column therefore offers `+ Add`, and a card can be taken off again — neither of
  // which means anything on a computed board, where the next tick would simply put the
  // member back.
  //
  // COLUMNS ARE UNBOUNDED. A state can hold every member at once (ai-automation-society
  // has 43,456 in `Lapsed`), so a column shows its TOTAL count and a page of cards, with
  // "load more" paging the rest. `col.total` is the truth; `col.cards.length` is only
  // what has been fetched.
  //
  // ONE COLUMN PER STATE, in the pipeline's own display order. The two terminals used to
  // share a split column; they don't any more. A split reads as a summary — fine when the
  // terminals are an archive, wrong when they are destinations you want to compare
  // side-by-side with the states leading into them, which is what a journey board is for.
  //
  // Pure layout: each drop zone calls `onMove(card_id, state_key)`. No fetching — the
  // page owns board state and the paging.
  let {
    board,
    nowMs = Date.now(),
    loadingMore = new Set<string>(),
    sorts = {},
    onAdd,
    onRemove,
    onMove,
    onLoadMore,
    onOpen,
    onSort,
    onUnpin,
  }: {
    board: PipelineBoardData;
    nowMs?: number;
    /** state keys currently fetching another page */
    loadingMore?: Set<string>;
    /** `state_key -> sort`, holding only the columns a reader has re-ordered. Absent
     *  means the column is in the state's own declared order (`col.sort`). */
    sorts?: Record<string, string>;
    onMove?: ((cardId: number, stateKey: string) => void) | undefined;
    onLoadMore?: ((stateKey: string) => void) | undefined;
    onOpen?: ((cardId: number) => void) | undefined;
    onSort?: ((stateKey: string, sort: string) => void) | undefined;
    /** Release a card's manual pin — raised from the tile's pin badge on hover. */
    onUnpin?: ((cardId: number) => void) | undefined;
    /** MANUAL BOARDS ONLY: put someone in this column. Absent on a computed board,
     *  which is what hides the affordance rather than a flag read down here. */
    onAdd?: ((stateKey: string) => void) | undefined;
    /** MANUAL BOARDS ONLY: take a card off the board entirely. */
    onRemove?: ((cardId: number) => void) | undefined;
  } = $props();

  // THE ORDERINGS — the same eight in every column of every pipeline, in this order.
  // What varies per column is only which one it OPENS in (`col.sort`, the state's
  // declared default), never which are on offer: a reader should not have to learn that
  // one column can be asked a question another cannot.
  //
  // Four axes, each read both ways: how MUCH they have written, how long they have been
  // here, how RECENTLY they were seen, and what they pay.
  //
  // THE LABELS ARE NOT THE API'S WORDS, and the first pair is why. `most_activity` counts
  // lifetime posts + comments; `most_active` reads their last-seen date. Two genuinely
  // different questions that routinely disagree — a member with 300 posts who stopped last
  // year tops one and bottoms the other — under names a letter apart, which nobody should
  // have to hold in their head at a dropdown. "Contributions" says it counts things they
  // wrote; "active/inactive" says it is about time. The wire values keep the old names, so
  // this is presentation only and no stored column default has to move.
  //
  // `sort=priority` is gone: it meant a different measure in every column, so it was the
  // one option whose effect nobody could predict.
  const SORT_OPTIONS: { value: string; label: string }[] = [
    { value: 'most_activity', label: 'Most contributions' },
    { value: 'least_activity', label: 'Fewest contributions' },
    { value: 'newest_member', label: 'Newest member' },
    { value: 'oldest_member', label: 'Oldest member' },
    { value: 'most_active', label: 'Recently active' },
    { value: 'least_active', label: 'Longest inactive' },
    { value: 'most_paying', label: 'Most paying' },
    { value: 'least_paying', label: 'Least paying' },
    // Lifetime spend, and the only axis without a `least_` twin — see `_SORTS`. Worded
    // to match the card, which prints "paid £343" under the plan pill.
    { value: 'most_paid', label: 'Most paid' },
    // Board order, not member order — the two that answer "when did this card get here"
    // rather than "what is this member like". A MANUAL board's states declare
    // `newest_card` as their default, so a member just added is at the top.
    { value: 'newest_card', label: 'Recently added' },
    { value: 'oldest_card', label: 'Longest on board' },
  ];

  // Whether the ACTIVE community has refused us its members page. The predicate is
  // `rosterMissing()` in the community store — shared with `<RosterAccessBanner>`,
  // which this column now mounts, so the column and the chip inside it cannot disagree
  // about whether there is anything to prompt for.
  const noRoster = $derived(rosterMissing());

  // --- viewport fit -------------------------------------------------------------------
  // The board is bounded to the space left below it and scrolls INTERNALLY: columns
  // scroll on their own, so the headers and the pipeline switcher stay put instead of
  // the whole page growing to the height of the longest column.
  //
  // Measured rather than a `calc(100dvh - <constant>)`: everything above the board is not
  // a fixed height — the shell's community header wraps to a second line when the trial
  // and roster chips do not fit beside the name, and the switcher wraps on narrow
  // viewports. Reading the board's own top offset is correct under all of those; a
  // hardcoded offset would be wrong the moment either wraps.
  const MIN_BOARD_H = 320; // never collapse below this on a short viewport
  // Matches the page wrapper's `py-8` (2rem). That padding sits BELOW the board, so
  // anything smaller here pushes the page just past the viewport and hands the window a
  // scrollbar — which is the exact thing this measurement exists to remove.
  const BOTTOM_GAP = 32;

  let boardEl = $state<HTMLDivElement | null>(null);
  let boardH = $state<number | null>(null);

  function measure(): void {
    const el = boardEl;
    if (!el) return;
    const top = el.getBoundingClientRect().top;
    boardH = Math.max(MIN_BOARD_H, window.innerHeight - top - BOTTOM_GAP);
  }

  $effect(() => {
    void board; // re-measure when the pipeline or community switches
    measure();
    window.addEventListener('resize', measure);

    // …and whenever anything ABOVE the board changes height. `measure()` reads this
    // element's `top`, so it is only correct for as long as the content above it is
    // stable — and it is not: the revenue tiles land on their own request, after the
    // board has already sized itself, and the board then keeps a height computed for a
    // position it no longer occupies. The page overflows by exactly the height of the
    // thing that appeared, which is the page-level scrollbar this measurement exists to
    // prevent. A resize listener never sees it because the window never resized.
    //
    // Guarded against the feedback loop this invites: setting `boardH` changes the
    // document's height, which re-fires the observer, so a re-measure that lands within
    // a pixel of the current value is dropped rather than written back.
    const ro = new ResizeObserver(() => {
      const before = boardH;
      measure();
      if (before != null && boardH != null && Math.abs(before - boardH) <= 1) boardH = before;
    });
    ro.observe(document.body);

    return () => {
      window.removeEventListener('resize', measure);
      ro.disconnect();
    };
  });

  let dragging = $state<number | null>(null); // card_id currently dragged
  let overKey = $state<string | null>(null); // which state column is hovered

  function readId(e: DragEvent): number | null {
    const raw = e.dataTransfer?.getData('text/plain');
    const id = raw ? Number(raw) : NaN;
    return Number.isFinite(id) ? id : null;
  }
  function allowDrop(e: DragEvent, stateKey: string): void {
    e.preventDefault();
    if (e.dataTransfer) e.dataTransfer.dropEffect = 'move';
    overKey = stateKey;
  }
  function leave(stateKey: string): void {
    if (overKey === stateKey) overKey = null;
  }
  function drop(e: DragEvent, stateKey: string): void {
    e.preventDefault();
    const id = readId(e);
    overKey = null;
    dragging = null;
    if (id != null) onMove?.(id, stateKey);
  }

  /** Compact count label — a column can hold five figures, and "43456" in a chip is
   *  noise where "43.5k" is a number you can read at a glance. */
  function short(n: number): string {
    if (n < 1000) return String(n);
    if (n < 10_000) return `${(n / 1000).toFixed(1).replace(/\.0$/, '')}k`;
    return `${Math.round(n / 1000)}k`;
  }

  const cardHandlers = {
    onOpen: (id: number): void => onOpen?.(id),
    onDragStart: (id: number): void => {
      dragging = id;
    },
    onDragEnd: (): void => {
      dragging = null;
      overKey = null;
    },
  };
</script>

<!-- One state's cards + drop target. A snippet rather than inline markup so every column
     shares one drag/drop affordance and they cannot drift apart. -->
{#snippet dropZone(col: PipelineColumn)}
  {@const meta = STAGE_KIND_META[col.kind]}
  {@const more = col.total - col.cards.length}
  <!-- `min-h-0` is what actually lets this scroll: a flex child defaults to
       min-height:auto and would grow to fit its cards instead of overflowing. -->
  <div
    role="list"
    class="min-h-0 flex-1 space-y-2 overflow-y-auto rounded-box p-1.5 transition-colors {overKey === col.key
      ? 'bg-primary/[0.06] ring-2 ring-primary/40'
      : `ring-1 ${meta.ring}`} {dragging !== null ? 'min-h-[6rem]' : 'min-h-[3rem]'}"
    ondragover={(e) => allowDrop(e, col.key)}
    ondragleave={() => leave(col.key)}
    ondrop={(e) => drop(e, col.key)}
  >
    {#each col.cards as card (card.card_id)}
      <PipelineCardTile
        {card}
        {nowMs}
        {onUnpin}
        {onRemove}
        {...cardHandlers}
      />
    {/each}

    {#if col.cards.length === 0 && dragging === null}
      {#if col.needs_roster && noRoster}
        <!-- NOT "Empty" — this column is empty for a REASON we can name, and one the
             owner can fix in about thirty seconds. Saying "Empty" here tells them the
             product found nobody, when the truth is that Skool will not show us anybody.
             Only when the roster really is missing: with access these columns fill
             normally and the note would be a lie. -->
        <div class="px-2 py-3 text-center">
          <p class="text-[0.7rem] leading-relaxed text-base-content/60">
            {#if col.kind === 'lost'}
              We can’t see who left yet.
            {:else}
              We can’t see members who haven’t posted yet.
            {/if}
          </p>
          <!-- The SAME chip the shell's header carries, not a second explanation of the
               same problem. It used to be three lines of prose ending in "the banner at
               the top has the details" — a pointer to a strip that no longer exists,
               which is exactly how a second copy of an explanation goes stale.

               `dismissible={false}`: dismissing this one would leave the column saying
               "we can't see this" with nothing to click. `showLabel={false}`: the line
               above already said it, and the full label does not fit a column. -->
          <div class="mt-2 flex justify-center">
            <RosterAccessBanner dismissible={false} showLabel={false} />
          </div>
        </div>
      {:else}
        <p class="px-2 py-3 text-center text-[0.7rem] text-base-content/40">Empty</p>
      {/if}
    {:else if more > 0}
      <button
        class="w-full rounded-field py-1.5 text-center text-[0.7rem] text-base-content/60 transition-colors hover:bg-base-200 disabled:opacity-60"
        onclick={() => onLoadMore?.(col.key)}
        disabled={loadingMore.has(col.key) || !onLoadMore}
      >
        {#if loadingMore.has(col.key)}
          <span class="loading loading-spinner loading-xs"></span>
        {:else}
          Load {short(Math.min(more, 50))} more · {short(more)} left
        {/if}
      </button>
    {/if}
  </div>
{/snippet}

<!-- Horizontal kanban: the column count is the pipeline's, so this scrolls sideways
     rather than trying to fit N columns into the viewport width — and is bounded to the
     viewport vertically, so each column scrolls in place. -->
<div
  bind:this={boardEl}
  class="overflow-x-auto overflow-y-hidden pb-2"
  style={boardH != null ? `height:${boardH}px` : undefined}
>
  <div class="flex h-full min-w-max items-stretch gap-3">
    <!-- HIDDEN COLUMNS ARE DROPPED HERE, not upstream. The board data keeps them, because
         the page still needs them to find a card by id and to move one; only the kanban
         declines to draw them. Engagement's catch-all is the case: every member who has
         never contributed holds a card there, and it is not a column anyone works. -->
    {#each board.stages.filter((c) => !c.hidden) as col (col.key)}
      {@const meta = STAGE_KIND_META[col.kind]}
      <section class="flex h-full min-h-0 w-[17rem] shrink-0 flex-col">
        <!-- column header -->
        <div class="mb-2 flex items-center gap-2 px-0.5">
          <span class="h-2 w-2 shrink-0 rounded-full {meta.dot}"></span>
          <h2
            class="truncate font-display text-sm font-semibold tracking-tight"
            title={col.label}
          >{col.label}</h2>
          <span
            class="shrink-0 rounded-full px-1.5 py-0.5 font-mono text-[0.62rem] tabular-nums {meta.count}"
            title="{col.total} cards"
          >{short(col.total)}</span>

          <!-- Per-column ordering. A bare <select> rather than a menu: it is a handful of
               fixed options in a 17rem column, and the native control is the one thing
               that already fits, keyboards properly and says what it is.

               THE SELECT IS THE WHOLE TARGET. It used to sit inside a <label> next to the
               icon, which is what made this awkward to hit: clicking a label FOCUSES the
               select it wraps but does not open it, so only the ~90px of text actually
               worked and everything around it swallowed the click. Now the icon is
               absolutely positioned behind the control with `pointer-events-none`, and the
               select fills the box — every pixel of it opens the menu.

               8.5rem because "Fewest contributions" is the longest label and truncating
               the closed state to "Fewest contr…" would undo the point of naming these
               clearly. `appearance-none` drops the native arrow so the icon is the only
               affordance rather than the second one. `col.sort` is the state's
               declaration, so with no override the value shown IS the truth. -->
          {#if onSort}
            <div class="relative ml-auto flex min-w-0 shrink items-center">
              <svg
                class="pointer-events-none absolute left-1.5 h-3.5 w-3.5 opacity-55"
                viewBox="0 0 24 24" fill="none"
                stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"
              ><path d="m3 16 4 4 4-4" /><path d="M7 20V4" /><path d="M11 4h4" /><path d="M11 8h7" /><path d="M11 12h10" /></svg>
              <select
                class="h-7 w-full min-w-0 max-w-[9.75rem] cursor-pointer appearance-none truncate rounded-field bg-transparent py-0 pl-7 pr-1.5 text-[0.68rem] text-base-content/60 outline-none transition-colors hover:bg-base-200 hover:text-base-content focus-visible:ring-2 focus-visible:ring-primary/40 disabled:cursor-not-allowed disabled:opacity-50"
                aria-label="Sort the {col.label} column"
                title="Sort this column"
                value={sorts[col.key] ?? col.sort ?? 'most_activity'}
                disabled={loadingMore.has(col.key)}
                onchange={(e) => onSort(col.key, (e.currentTarget as HTMLSelectElement).value)}
              >
                {#each SORT_OPTIONS as opt (opt.value)}
                  <option value={opt.value}>{opt.label}</option>
                {/each}
              </select>
            </div>
          {/if}
        </div>

        <!-- ADD, AS THE COLUMN'S SUBHEADER. It began beside the count, which left a
             17rem header holding a dot, a label, a count, a button and a sort select —
             and the label is what gave way, so every column read "Invi…", "Onboar…",
             "Pas…". On its own line under the title it cannot compete for that width at
             all, while still sitting with the column's other controls rather than at the
             far end of a scrolled list of cards.

             Ghosted like the `+` in the switcher row, and for the same reason: it is not
             a card, so it must not look like one. -->
        {#if onAdd}
          <button
            class="mb-1.5 w-full rounded-field py-1 text-center text-[0.7rem] text-base-content/40 transition-colors hover:bg-base-200/70 hover:text-base-content/80"
            title="Add a member to {col.label}"
            onclick={() => onAdd(col.key)}
          >+ Add a member</button>
        {/if}

        {@render dropZone(col)}
      </section>
    {/each}
  </div>
</div>
